import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { withLoadingToast } from "utils/withLoadingToast";
import {
  acceptQuotation,
  cancelQuotation,
  rejectQuotation,
  resendQuotation,
  sendQuotation,
} from "features/Quotations/services/QuotationService";
import type { Quotation } from "features/Quotations/types/quotation.types";

function axiosErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string } } };
  return ax.response?.data?.message ?? fallback;
}

function workflowErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message
    ? err.message
    : axiosErrorMessage(err, fallback);
}

export type UseQuotationWorkflowOptions = {
  /** Called after a successful send / resend / accept / cancel / reject */
  onSuccess: () => void | Promise<void>;
  /**
   * Detail page: always the loaded quotation.
   * List: omit; pass a row into each `open*` call instead.
   */
  contextQuotation?: Quotation | null;
};

export interface QuotationWorkflow {
  workflowBusy: boolean;
  /** Quotation targeted by open modals / confirm actions */
  activeQuotation: Quotation | undefined;
  sendConfirmOpen: boolean;
  setSendConfirmOpen: (open: boolean) => void;
  resendConfirmOpen: boolean;
  setResendConfirmOpen: (open: boolean) => void;
  acceptModalOpen: boolean;
  setAcceptModalOpen: (open: boolean) => void;
  cancelModalOpen: boolean;
  setCancelModalOpen: (open: boolean) => void;
  rejectModalOpen: boolean;
  setRejectModalOpen: (open: boolean) => void;
  openSendConfirm: (quotation?: Quotation) => void;
  openResendConfirm: (quotation?: Quotation) => void;
  openAcceptModal: (quotation?: Quotation) => void;
  openCancelModal: (quotation?: Quotation) => void;
  openRejectModal: (quotation?: Quotation) => void;
  runSend: () => Promise<void>;
  runResend: () => Promise<void>;
  runAccept: () => Promise<void>;
  runCancel: (reason?: string) => Promise<void>;
  runReject: (rejectReason?: string) => Promise<void>;
}

export function useQuotationWorkflow({
  onSuccess,
  contextQuotation,
}: UseQuotationWorkflowOptions): QuotationWorkflow {
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [target, setTarget] = useState<Quotation | undefined>();
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const activeQuotation: Quotation | undefined =
    contextQuotation != null ? contextQuotation : target;

  const clearListTarget = useCallback(() => {
    if (contextQuotation == null) setTarget(undefined);
  }, [contextQuotation]);

  const afterSuccess = useCallback(async () => {
    await onSuccess();
    clearListTarget();
  }, [onSuccess, clearListTarget]);

  const getQuotation = useCallback((): Quotation | undefined => {
    return contextQuotation != null ? contextQuotation : target;
  }, [contextQuotation, target]);

  const openSendConfirm = useCallback((quotation?: Quotation) => {
    if (quotation) setTarget(quotation);
    setSendConfirmOpen(true);
  }, []);

  const openResendConfirm = useCallback((quotation?: Quotation) => {
    if (quotation) setTarget(quotation);
    setResendConfirmOpen(true);
  }, []);

  const openAcceptModal = useCallback((quotation?: Quotation) => {
    if (quotation) setTarget(quotation);
    setAcceptModalOpen(true);
  }, []);

  const openCancelModal = useCallback((quotation?: Quotation) => {
    if (quotation) setTarget(quotation);
    setCancelModalOpen(true);
  }, []);

  const openRejectModal = useCallback((quotation?: Quotation) => {
    if (quotation) setTarget(quotation);
    setRejectModalOpen(true);
  }, []);

  const runSend = useCallback(async () => {
    const q = getQuotation();
    if (!q) return;
    setWorkflowBusy(true);
    try {
      await withLoadingToast(
        "Sending quotation…",
        async () => {
          const response = await sendQuotation(q.id);
          if (!response.success) {
            throw new Error(response.message || "Send failed");
          }
          setSendConfirmOpen(false);
          await afterSuccess();
        },
        "Quotation sent",
        "Send failed",
        (err) => workflowErrorMessage(err, "Send failed"),
      );
    } finally {
      setWorkflowBusy(false);
    }
  }, [getQuotation, afterSuccess]);

  const runResend = useCallback(async () => {
    const q = getQuotation();
    if (!q) return;
    setWorkflowBusy(true);
    try {
      await withLoadingToast(
        "Resending quotation…",
        async () => {
          const response = await resendQuotation(q.id);
          if (!response.success) {
            throw new Error(response.message || "Resend failed");
          }
          setResendConfirmOpen(false);
          await afterSuccess();
        },
        "Quotation resent",
        "Resend failed",
        (err) => workflowErrorMessage(err, "Resend failed"),
      );
    } finally {
      setWorkflowBusy(false);
    }
  }, [getQuotation, afterSuccess]);

  const runAccept = useCallback(async () => {
    const q = getQuotation();
    if (!q) return;
    setWorkflowBusy(true);
    try {
      const response = await acceptQuotation(q.id);
      if (response.success) {
        toast.success(response.message || "Quotation accepted");
        setAcceptModalOpen(false);
        await afterSuccess();
      } else {
        toast.error(response.message || "Accept failed");
      }
    } catch (err: unknown) {
      toast.error(axiosErrorMessage(err, "Accept failed"));
    } finally {
      setWorkflowBusy(false);
    }
  }, [getQuotation, afterSuccess]);

  const runCancel = useCallback(
    async (reason?: string) => {
      const q = getQuotation();
      if (!q) return;
      setWorkflowBusy(true);
      try {
        const response = await cancelQuotation(q.id, {
          cancelReason: reason,
        });
        if (response.success) {
          toast.success(response.message || "Quotation cancelled");
          setCancelModalOpen(false);
          await afterSuccess();
        } else {
          toast.error(response.message || "Cancel failed");
        }
      } catch (err: unknown) {
        toast.error(axiosErrorMessage(err, "Cancel failed"));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [getQuotation, afterSuccess],
  );

  const runReject = useCallback(
    async (rejectReason?: string) => {
      const q = getQuotation();
      if (!q) return;
      setWorkflowBusy(true);
      try {
        const response = await rejectQuotation(q.id, {
          rejectReason: rejectReason?.trim() || null,
        });
        if (response.success) {
          toast.success(response.message || "Quotation rejected");
          setRejectModalOpen(false);
          await afterSuccess();
        } else {
          toast.error(response.message || "Reject failed");
        }
      } catch (err: unknown) {
        toast.error(axiosErrorMessage(err, "Reject failed"));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [getQuotation, afterSuccess],
  );

  return {
    workflowBusy,
    activeQuotation,
    sendConfirmOpen,
    setSendConfirmOpen,
    resendConfirmOpen,
    setResendConfirmOpen,
    acceptModalOpen,
    setAcceptModalOpen,
    cancelModalOpen,
    setCancelModalOpen,
    rejectModalOpen,
    setRejectModalOpen,
    openSendConfirm,
    openResendConfirm,
    openAcceptModal,
    openCancelModal,
    openRejectModal,
    runSend,
    runResend,
    runAccept,
    runCancel,
    runReject,
  };
}
